import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTenant } from "../contexts/TenantContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { CountryCodeSelect } from "./CountryCodeSelect";
import { Plane, AlertCircle, Eye, EyeOff, Mail, Lock, User } from "lucide-react";
import { api } from "../utils/api";
import { toast } from "sonner";
import ResetPasswordModal from "./ResetPassword";

export const LoginPage = () => {
    const { login, setUser, setToken } = useAuth();
    const { brandName, logoUrl } = useTenant();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showRegister, setShowRegister] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showRegPassword, setShowRegPassword] = useState(false);
    const [registerData, setRegisterData] = useState({
        name: "",
        email: "",
        password: "",
        phone: "",
        country_code: "+91",
        passport_number: "",
        customer_type: "B2C Guest",
    });
    const [showModal, setShowModal] = useState(false);

    // Temp Password Change States
    const [requireTempPasswordChange, setRequireTempPasswordChange] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        const result = await login(email, password);

        if (!result.success) {
            setError(result.error || "Login failed");
        } else if (result.requirePasswordChange) {
            setRequireTempPasswordChange(true);
        }

        setLoading(false);
    };

    const handleTempPasswordChange = async (e) => {
        e.preventDefault();
        setError("");

        if (!newPassword || !confirmPassword) {
            setError("All fields are required");
            return;
        }
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }
        if (newPassword.length < 6) {
            setError("Password must be at least 6 characters");
            return;
        }

        setLoading(true);
        try {
            const response = await api.changeTempPassword(email, password, newPassword);
            if (response.data.success) {
                setUser(response.data.user);
                setToken(response.data.token);
                localStorage.setItem("user", JSON.stringify(response.data.user));
                localStorage.setItem("token", response.data.token);
                toast.success("Password set successfully!");
            }
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to change temporary password");
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            await api.registerCustomer(registerData);
            toast.success("Account created successfully! Please login.");
            setShowRegister(false);
            setEmail(registerData.email);
            setRegisterData({
                name: "",
                email: "",
                password: "",
                phone: "",
                country_code: "+91",
                passport_number: "",
                customer_type: "B2C Guest",
            });
        } catch (error) {
            setError(error.response?.data?.detail || "Registration failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen md:h-screen md:overflow-hidden flex flex-col md:flex-row bg-slate-50 font-sans">
            {/* Left Side: Auth Form Container */}
            <div className="hidden md:flex md:w-1/2 relative flex-col justify-between p-8 lg:p-12 text-white overflow-hidden bg-slate-900 h-screen">
                {/* Background image & gradient overlay */}
                <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-10000 ease-out hover:scale-105"
                    style={{ backgroundImage: 'url("/travel-hero.png")' }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-orange-600/70 via-slate-900/80 to-slate-950/95 mix-blend-multiply" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-60" />

                {/* Top Corner Branding Accent */}
                <div className="relative z-10 flex items-center gap-2">
                    <span className="bg-orange-500/20 text-orange-400 text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider border border-orange-500/30 backdrop-blur-md">
                        ✈️ Let's plan your next travel
                    </span>
                </div>

                {/* Center Content: Headline & Categories list */}
                <div className="relative z-10 my-auto max-w-lg space-y-4">
                    <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight">
                        Plan complete, <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">budget-friendly</span> trips.
                    </h2>
                    <p className="text-slate-300 text-sm leading-relaxed">
                        From visas to trains, flights, hotels, holiday packages, and local transport — we plan and manage everything for your ultimate peace of mind.
                    </p>

                    {/* Grid of travel categories */}
                    <div className="grid grid-cols-2 gap-2.5 pt-2">
                        <div className="flex items-center space-x-2 bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg border border-white/10 backdrop-blur-sm transition-all group">
                            <span className="text-lg group-hover:scale-110 transition-transform">✈️</span>
                            <span className="font-semibold text-xs lg:text-sm">Flights</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg border border-white/10 backdrop-blur-sm transition-all group">
                            <span className="text-lg group-hover:scale-110 transition-transform">🏨</span>
                            <span className="font-semibold text-xs lg:text-sm">Hotels</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg border border-white/10 backdrop-blur-sm transition-all group">
                            <span className="text-lg group-hover:scale-110 transition-transform">📄</span>
                            <span className="font-semibold text-xs lg:text-sm">Visas</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg border border-white/10 backdrop-blur-sm transition-all group">
                            <span className="text-lg group-hover:scale-110 transition-transform">🤝</span>
                            <span className="font-semibold text-xs lg:text-sm">MICE Events</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg border border-white/10 backdrop-blur-sm transition-all group">
                            <span className="text-lg group-hover:scale-110 transition-transform">🚆</span>
                            <span className="font-semibold text-xs lg:text-sm">Trains & Buses</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg border border-white/10 backdrop-blur-sm transition-all group">
                            <span className="text-lg group-hover:scale-110 transition-transform">🗺️</span>
                            <span className="font-semibold text-xs lg:text-sm">Sightseeing</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg border border-white/10 backdrop-blur-sm transition-all group">
                            <span className="text-lg group-hover:scale-110 transition-transform">🎁</span>
                            <span className="font-semibold text-xs lg:text-sm">Holiday Packages</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg border border-white/10 backdrop-blur-sm transition-all group">
                            <span className="text-lg group-hover:scale-110 transition-transform">🚗</span>
                            <span className="font-semibold text-xs lg:text-sm">Local Transport</span>
                        </div>
                    </div>
                </div>

                {/* Bottom Content: Glassmorphic stats card */}
                <div className="relative z-10 bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl flex items-center justify-between gap-4">
                    <div className="text-center flex-1">
                        <span className="block text-2xl font-bold text-orange-400">14k+</span>
                        <span className="text-xs text-slate-300">Trips Completed</span>
                    </div>
                    <div className="h-8 w-px bg-white/20" />
                    <div className="text-center flex-1">
                        <span className="block text-2xl font-bold text-orange-400">99.4%</span>
                        <span className="text-xs text-slate-300">Happy Travelers</span>
                    </div>
                    <div className="h-8 w-px bg-white/20" />
                    <div className="text-center flex-1">
                        <span className="block text-2xl font-bold text-orange-400">100%</span>
                        <span className="text-xs text-slate-300">Budget Friendly</span>
                    </div>
                </div>
            </div>

            {/* Right Side: Hero Visual Panel */}
            <div className="w-full md:w-1/2 flex items-center justify-center p-6 md:p-12 lg:p-16 bg-white shadow-lg z-10 md:h-screen md:overflow-y-auto">
                <div className="w-full max-w-md space-y-8 animate-in fade-in slide-in-from-left duration-300">
                    {/* Header */}
                    <div className="flex flex-col space-y-2">
                        <div className="flex items-center justify-start mb-4">
                            <img
                                src={logoUrl || "/traveego-logo.jpeg"}
                                alt={brandName || "Traveego"}
                                className="h-12 w-auto max-w-[200px] object-contain rounded-md"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "/traveego-logo.jpeg";
                                }}
                            />
                        </div>
                        <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                            {showRegister ? "Create Account" : "Welcome Back"}
                        </h1>
                        <p className="text-sm text-slate-500">
                            {showRegister
                                ? "Register as a new customer to start planning your journeys"
                                : "Enter your credentials to access your account"}
                        </p>
                    </div>

                    {error && (
                        <Alert variant="destructive" className="bg-red-50 border-red-200 text-red-800 rounded-lg shadow-sm">
                            <AlertCircle className="h-4 w-4 text-red-600" />
                            <AlertDescription className="text-red-700">
                                {error}
                            </AlertDescription>
                        </Alert>
                    )}

                    {requireTempPasswordChange ? (
                        <form onSubmit={handleTempPasswordChange} className="space-y-5">
                            <div className="space-y-1">
                                <h2 className="text-xl font-bold text-slate-900">Create New Password</h2>
                                <p className="text-sm text-slate-500">
                                    Your account is using a temporary password. Choose a secure new password to continue.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="newPassword" className="text-sm font-medium text-slate-700">New Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                                    <Input
                                        id="newPassword"
                                        type={showNewPassword ? "text" : "password"}
                                        placeholder="••••••••"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                        className="pl-10 pr-10 h-10 border-slate-200 focus:border-orange-500 focus:ring-orange-500 rounded-lg transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 transition-colors"
                                    >
                                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">Confirm New Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                                    <Input
                                        id="confirmPassword"
                                        type={showConfirmPassword ? "text" : "password"}
                                        placeholder="••••••••"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        className="pl-10 pr-10 h-10 border-slate-200 focus:border-orange-500 focus:ring-orange-500 rounded-lg transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 transition-colors"
                                    >
                                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>

                            <Button
                                type="submit"
                                className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all"
                                disabled={loading}
                            >
                                {loading ? "Updating Password..." : "Update & Sign In"}
                            </Button>

                            <div className="text-center pt-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setRequireTempPasswordChange(false);
                                        setPassword("");
                                        setNewPassword("");
                                        setConfirmPassword("");
                                        setError("");
                                    }}
                                    className="text-sm font-medium text-slate-600 hover:text-orange-600 transition-colors"
                                >
                                    Back to Sign In
                                </button>
                            </div>
                        </form>
                    ) : !showRegister ? (
                        <form onSubmit={handleLogin} className="space-y-5">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email Address</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="name@example.com"
                                        value={email}
                                        name="email"
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="pl-10 h-10 border-slate-200 focus:border-orange-500 focus:ring-orange-500 rounded-lg transition-all"
                                        data-testid="login-email-input"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                    <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
                                </div>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                                    <Input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="••••••••"
                                        value={password}
                                        name="password"
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="pl-10 pr-10 h-10 border-slate-200 focus:border-orange-500 focus:ring-orange-500 rounded-lg transition-all"
                                        data-testid="login-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 transition-colors"
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>

                            <Button
                                type="submit"
                                className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all"
                                disabled={loading}
                                data-testid="login-submit-button"
                            >
                                {loading ? "Signing in..." : "Sign In"}
                            </Button>

                            <div className="flex flex-col sm:flex-row justify-between items-center pt-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowRegister(true);
                                        setError("");
                                    }}
                                    className="text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors"
                                >
                                    New customer? Create account
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(true)}
                                    className="text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors"
                                >
                                    Forgot Password?
                                </button>
                            </div>
                        </form>
                    ) : (
                        <form onSubmit={handleRegister} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="reg-name" className="text-sm font-medium text-slate-700">Full Name</Label>
                                <div className="relative">
                                    <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                                    <Input
                                        id="reg-name"
                                        placeholder="John Doe"
                                        value={registerData.name}
                                        onChange={(e) =>
                                            setRegisterData({
                                                ...registerData,
                                                name: e.target.value,
                                            })
                                        }
                                        required
                                        className="pl-10 h-10 border-slate-200 focus:border-orange-500 focus:ring-orange-500 rounded-lg transition-all"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="reg-email" className="text-sm font-medium text-slate-700">Email Address</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                                    <Input
                                        id="reg-email"
                                        type="email"
                                        placeholder="name@example.com"
                                        value={registerData.email}
                                        onChange={(e) =>
                                            setRegisterData({
                                                ...registerData,
                                                email: e.target.value,
                                            })
                                        }
                                        required
                                        className="pl-10 h-10 border-slate-200 focus:border-orange-500 focus:ring-orange-500 rounded-lg transition-all"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="reg-phone" className="text-sm font-medium text-slate-700">Phone Number</Label>
                                <div className="flex gap-2">
                                    <CountryCodeSelect
                                        value={registerData.country_code}
                                        onChange={(value) =>
                                            setRegisterData({
                                                ...registerData,
                                                country_code: value,
                                            })
                                        }
                                        testId="register-country-code-select"
                                    />
                                    <Input
                                        id="reg-phone"
                                        value={registerData.phone}
                                        onChange={(e) =>
                                            setRegisterData({
                                                ...registerData,
                                                phone: e.target.value,
                                            })
                                        }
                                        required
                                        className="flex-1 h-10 border-slate-200 focus:border-orange-500 focus:ring-orange-500 rounded-lg transition-all"
                                        placeholder="9876543210"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="reg-passport" className="text-sm font-medium text-slate-700">Passport Number</Label>
                                    <Input
                                        id="reg-passport"
                                        placeholder="Passport (Optional)"
                                        value={registerData.passport_number}
                                        onChange={(e) =>
                                            setRegisterData({
                                                ...registerData,
                                                passport_number: e.target.value,
                                            })
                                        }
                                        className="h-10 border-slate-200 focus:border-orange-500 focus:ring-orange-500 rounded-lg transition-all"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="reg-customer-type" className="text-sm font-medium text-slate-700">Customer Type</Label>
                                    <select
                                        id="reg-customer-type"
                                        value={registerData.customer_type}
                                        onChange={(e) =>
                                            setRegisterData({
                                                ...registerData,
                                                customer_type: e.target.value,
                                            })
                                        }
                                        className="w-full h-10 px-3 border border-slate-200 focus:border-orange-500 focus:ring-orange-500 rounded-lg bg-white text-sm transition-all focus:outline-none"
                                    >
                                        <option value="B2C Guest">B2C Guest</option>
                                        <option value="B2B">Travel Agent (B2B)</option>
                                        <option value="Corporate">Corporate</option>
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="reg-password" className="text-sm font-medium text-slate-700">Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                                    <Input
                                        id="reg-password"
                                        type={showRegPassword ? "text" : "password"}
                                        placeholder="Create a strong password"
                                        value={registerData.password}
                                        onChange={(e) =>
                                            setRegisterData({
                                                ...registerData,
                                                password: e.target.value,
                                            })
                                        }
                                        required
                                        className="pl-10 pr-10 h-10 border-slate-200 focus:border-orange-500 focus:ring-orange-500 rounded-lg transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowRegPassword(!showRegPassword)}
                                        className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 transition-colors"
                                    >
                                        {showRegPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>

                            <Button
                                type="submit"
                                className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all"
                                disabled={loading}
                            >
                                {loading ? "Creating Account..." : "Create Account"}
                            </Button>

                            <div className="text-center pt-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowRegister(false);
                                        setError("");
                                    }}
                                    className="text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors"
                                >
                                    Already have an account? Sign in
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>


            <ResetPasswordModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
            />
        </div>
    );
};

